import { Component, HostListener, Input, OnInit, OnDestroy, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../../../auth/auth.service';
import { MenuItem } from '../menu-item';
import { AppSettings } from '../../../../app.settings';
import { Settings } from '../../../../app.settings.model';
import { InternalScoringService } from '../../../../../portal/internal-scoring/internal-scoring.service';
import { QueueService } from '../../../../../shared/notifications/queue.service';
import { JwtUtils } from '../../../../../auth/utilities/jwtUtils';
import { SideNavSignalRService } from './side-nav-signalr.service';

@Component({
  selector: 'app-side-nav-item',
  templateUrl: './side-nav-item.component.html',
  styleUrls: ['./side-nav-item.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SideNavItemComponent implements OnInit, OnDestroy {
  @Input() menuItem: MenuItem;
  queueCount: number;
  isInternal: boolean;
  isUpdate: boolean;
  @Input() nestingLevel = 0;
  @Input() testIdPath = '';
  @Output() changeExpanded = new EventEmitter<any>();

  public get testId(): string {
    const label = this.menuItem && this.menuItem.primaryText ? this.menuItem.primaryText : 'item';
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return this.testIdPath ? this.testIdPath + '-' + slug : slug;
  }

  isSmallScreen = false;
  public isErrorNumber = false;
  public settings: Settings;

  private subscription: any;

  public get isVisible() {
    if (this.menuItem.excludeRoles && this.menuItem.excludeRoles.length > 0) {
      for (const role of this.menuItem.excludeRoles) {
        if(JwtUtils.hasRoleByName(role)) {
          return false;
        };
      }
    }

    if (!this.menuItem.roles || this.menuItem.roles.length === 0) {
      return true;
    }

    for (const role of this.menuItem.roles) {
      if(JwtUtils.hasRoleByName(role)) {
        return true;
      };
    }

    return false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.checkScreenWidth();
  }

  constructor(
    public internalScoringService: InternalScoringService,
    public queueService: QueueService,
    public router: Router,
    private authService: AuthService,
    private appSettings: AppSettings,
    private sideNavSignalRService: SideNavSignalRService,
  ) {
    this.settings = this.appSettings.settings;
  }

  ngOnInit() {
    if (this.menuItem.queue) {
      if (this.menuItem.queue == 'internal') {
        this.isInternal = true;
        this.isUpdate = false;
      } else if (this.menuItem.queue == 'external') {
        this.isInternal = false;
        this.isUpdate = false;
      } else if (this.menuItem.queue == 'update') {
        this.isUpdate = true;
        this.isInternal = true;
      } else if (this.menuItem.queue == 'notifications') {
        this.sideNavSignalRService.initializeConnection();
        const currentUrl = this.router.url.toLowerCase();
        if (currentUrl.includes('/portal')) {
          this.subscription = this.sideNavSignalRService.getFactoringNotificationCount().subscribe(count => {
            this.queueCount = count;
          });
        } else if (currentUrl.includes('/fleet-manager')) {
          this.subscription = this.sideNavSignalRService.getTransportationNotificationCount().subscribe(count => {
            this.queueCount = count;
          });
        } else {
          this.getNotificationCounts();
          this.subscription = this.sideNavSignalRService.getNotificationCount().subscribe(count => {
            this.queueCount = count;
          });
        }
      } else if(this.menuItem.queue == 'notificationsErrors') {
        this.isErrorNumber = true;
        this.sideNavSignalRService.initializeConnection();
        const currentUrl = this.router.url.toLowerCase();
        if (currentUrl.includes('/portal')) {
          this.subscription = this.sideNavSignalRService.getFactoringErrorCount().subscribe(count => {
            this.queueCount = count;
          });
        } else if (currentUrl.includes('/fleet-manager')) {
          this.subscription = this.sideNavSignalRService.getTransportationErrorCount().subscribe(count => {
            this.queueCount = count;
          });
        } else {
          this.getNotificationErrorCounts();
          this.subscription = this.sideNavSignalRService.getErrorCount().subscribe(count => {
            this.queueCount = count;
          });
        }
      } else if(this.menuItem.queue == 'reminders') {
        this.sideNavSignalRService.initializeConnection();
        this.subscription = this.sideNavSignalRService.getReminderCount().subscribe(count => {
          this.queueCount = count;
        });
      }

      if (this.menuItem.queue != 'notificationsErrors' && this.menuItem.queue != 'notifications' && this.menuItem.queue != 'reminders') {
        this.getRequestCounts();
        this.subscription = this.sideNavSignalRService.getQueueCountByType(this.menuItem.queue).subscribe(count => {
          this.queueCount = count;
        });
      }
    }
    this.menuItem.isExpanded = this.isActive(this.menuItem) ? true : false;
    this.checkScreenWidth();
  }

  onItemClick() {
    this.changeExpanded.emit(this.menuItem)
    if(this.isSmallScreen){
      this.settings.theme.showMenu = false;
    }
  }

  isActive(menuItem: MenuItem) {
    if (menuItem.routerLink === this.router.url.toLowerCase()) {
      return true;
    }

    if (menuItem.children) {
      for (let i = 0; i < menuItem.children.length; i++) {
        if (this.isActive(menuItem.children[i])) {
          return true;
        }
      }
    }
  }

  applyColor(menuItem: MenuItem) {
    if (menuItem.primaryColor) {
      const styles = {'color' : menuItem.primaryColor}
      return styles;
    }
  }
  applyAltColor(menuItem: MenuItem) {
    if (menuItem.secondaryColor) {
      const styles = {'color' : menuItem.secondaryColor}
      return styles;
    }
  }

  private checkScreenWidth(): void {
    const screenWidth = window.innerWidth;

    if (screenWidth <= 900) {
      this.isSmallScreen = true;
      this.settings.theme.showMenu = false;
    } else {
      this.isSmallScreen = false;
    }
  }
  getNotificationCounts() {
    this.queueService.getQueueItemCount()
      .toPromise()
      .then((data: any) => {
          this.queueCount = data;
    });
  }
  getNotificationErrorCounts() {
    this.queueService.getQueueResultErrorCount()
      .toPromise()
      .then((data: any) => {
          this.queueCount = data;
    });
  }
  getRequestCounts() {
    this.internalScoringService.getCreditRiskRequestCount(this.isInternal, this.isUpdate)
      .toPromise()
      .then((data: any) => {
          this.queueCount = data;
    });
  }
  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public editExpanded(changingItem){
    if(changingItem.children.length > 0){
      this.menuItem.children.forEach(item =>{
        if(item == changingItem){
          item.isExpanded = !item.isExpanded;
        }else{
          item.isExpanded = false;
        }
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/core/components/menu/side-nav/side-nav-item/side-nav-item.component.ts